import { useState, Fragment, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'app/components/core/Input';
import { Button, ButtonVariant, ButtonSize } from 'app/components/core/Button';
import { Field, useFormContext } from 'app/components/core/Form';
import { FileDropZone } from 'app/components/core/FileDropZone';
import { UploadButton } from 'app/components/composite/UploadButton';
import { MaskedTextarea } from 'app/components/composite/MaskedTextarea';

import * as styles from './PrivateKeySelector.css';

export function PrivateKeySelector({ namePrefix = null }) {
    const { t } = useTranslation();
    const { getFieldValue, setFieldValue } = useFormContext();
    const [keyFileName, setKeyFileName] = useState();
    const credentialType = getFieldValue('credentialType');
    const privateKeyName = namePrefix ? `${namePrefix}.privateKey` : 'privateKey';
    const keyPasswordName = namePrefix ? `${namePrefix}.keyPassword` : 'keyPassword';

    const resetKey = useCallback(() => {
        setFieldValue(privateKeyName, null);
        setFieldValue(keyPasswordName, null);
    }, [setFieldValue, privateKeyName, keyPasswordName]);

    useEffect(() => {
        resetKey();
    }, [resetKey, credentialType]);

    const handleUpload = (files) => {
        if (files.length > 1) {
            return;
        }
        setKeyFileName(files[0]?.name);
        const fileReader = new FileReader();
        fileReader.readAsText(files[0]);
        fileReader.onload = () => {
            setFieldValue(privateKeyName, fileReader.result);
        };
    };

    const handleDeleteKey = () => {
        setKeyFileName(null);
        resetKey();
    };

    return (
        <Fragment>
            <div className={styles.privateKeySelector}>
                <label>{t('forms:common.fields.privateKey.label')}</label>
                <div className={styles.keyFileUploadContainer}>
                    {keyFileName ? (
                        <div className={styles.uploadedKeySummary}>
                            <span>{t('admin:SnowflakeSettings.privateKey.fileName')}</span>
                            <span>{keyFileName}</span>
                            <Button
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.SMALL}
                                icon="delete"
                                onClick={handleDeleteKey}
                                label={t('common:delete')}></Button>
                        </div>
                    ) : (
                        <Fragment>
                            <FileDropZone
                                onDrop={handleUpload}
                                phrase={t('admin:SnowflakeSettings.privateKey.upload')}
                                className={styles.dropZone}>
                                <UploadButton
                                    onUpload={handleUpload}
                                    name={t('admin:SnowflakeSettings.privateKey.browse')}
                                    label={t('admin:SnowflakeSettings.privateKey.browse')}
                                    accept=".pem, .p8"
                                    size={ButtonSize.MEDIUM}
                                />
                            </FileDropZone>
                            <Field component={MaskedTextarea} name={privateKeyName} />
                        </Fragment>
                    )}
                </div>
            </div>
            <Field
                component={Input}
                type="password"
                name={keyPasswordName}
                label={t('admin:SnowflakeSettings.privateKeyPassword.label')}
                placeholder={t('admin:SnowflakeSettings.privateKeyPassword.placeholder')}
            />
        </Fragment>
    );
}
